import Box from '@mui/material/Box'
import TableCell from '@mui/material/TableCell'
import Tooltip from '@mui/material/Tooltip'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import { GridColDef } from '@mui/x-data-grid-premium'
import format from "date-fns/format"
import { DataLogger, DataLoggerDashboardStatistics, DataLoggerIntervalHistory, DataLoggerReadingUpload, ProjectSectionDataLogger } from 'src/api'
import { DynamicColumn, InnerLink, OuterLink } from 'src/components/ui'
import { capitalizeFirstLetter } from 'src/utils/formatters'
import { DATA_LOGGER_MEMORY_CAPACITY, classifyBatteryLevel, classifyDaysSinceActivation, getDaysSinceActivation, getMemoryCapacityDays } from 'src/helpers'

export namespace DataLoggerGridColumns {
  export const DeviceId: GridColDef<DataLogger> = {
    field: "deviceId",
    headerName: "Device ID",
    type: "string",
    minWidth: 200,
    editable: false,
    renderCell: ({ value }) => <InnerLink to={`/data-loggers/${value}`}>{value}</InnerLink>
  }

  export const Status: GridColDef<DataLogger> = {
    field: "isActive",
    headerName: "Status",
    type: "string",
    minWidth: 200,
    editable: false,
    valueGetter: ({ row }) => {
      const projectSectionsDataLoggers = (row?.projectSectionsDataLoggers || []).sort((a, b) => (
        +new Date(b.createdAt) - +new Date(a.createdAt)
      ))
      return projectSectionsDataLoggers[0]?.isActive || null
    },
    valueFormatter: ({ value }) => value === true ? 'Active' : 'Inactive',
  }

  export const Label: GridColDef<DataLogger> = {
    field: "projectSectionsDataLoggers[0].label",
    headerName: "Label",
    type: "string",
    minWidth: 200,
    editable: false,
    valueGetter: ({ row }) => {
      const sortedItems = (row?.projectSectionsDataLoggers || []).sort((a, b) => +new Date(b.createdAt) - +new Date(a.createdAt))
      const filteredItems = sortedItems.filter((item) => item.isActive)
      return filteredItems[0]?.label || null
    },
    valueFormatter: ({ value }) => value || '-',
  }

  export const Interval: GridColDef<DataLogger> = {
    field: "intervalHistory[0].intervalInHours",
    headerName: "Interval, hours",
    type: "string",
    minWidth: 150,
    editable: false,
    valueGetter: ({ row }) => {
      const activeInterval = (row?.intervalHistory || []).find((item) => item.isActive)
      return activeInterval?.intervalInHours || null
    },
    valueFormatter: ({ value }) => value || '-',
  }

  export const MatchReadingsToTimeInterval: GridColDef<DataLogger> = {
    field: "isMatchReadingsToTimeInterval",
    headerName: "Match readings to time interval",
    type: "boolean",
    minWidth: 150,
    editable: false,
  }

  export const FirstReadingDateTime: GridColDef<DataLogger> = {
    field: "projectSectionsDataLoggers[0].firstReadingAt",
    headerName: "First ping date",
    type: "string",
    minWidth: 200,
    editable: false,
    valueGetter: ({ row }) => {
      let firstReadingDate = +Infinity
      const projectSectionsDataLoggers: ProjectSectionDataLogger[] = row?.projectSectionsDataLoggers || []
      projectSectionsDataLoggers.forEach((item) => {
        const firstReading = item.firstReadingAt
        if (firstReading) {
          const firstReadingDateTime = new Date(firstReading).getTime()
          if (firstReadingDateTime < firstReadingDate) {
            firstReadingDate = firstReadingDateTime
          }
        }
      })

      return firstReadingDate
    },
    valueFormatter: ({ value }) => {
      if (value === +Infinity) return "-"
      return value ? format(new Date(value), "PPpp") : "-"
    },
  }

  export const LastReadingDateTime: GridColDef<DataLogger> = {
    field: "projectSectionsDataLoggers[0].lastReadingAt",
    headerName: "Last ping date",
    type: "string",
    minWidth: 200,
    editable: false,
    valueGetter: ({ row }) => {
      let lastReadingDate = -Infinity
      const projectSectionsDataLoggers: ProjectSectionDataLogger[] = row?.projectSectionsDataLoggers || []
      projectSectionsDataLoggers.forEach((item) => {
        const lastReading = item.lastReadingAt
        if (lastReading) {
          const lastReadingDateTime = new Date(lastReading).getTime()
          if (lastReadingDateTime > lastReadingDate) {
            lastReadingDate = lastReadingDateTime
          }
        }
      })

      return lastReadingDate
    },
    valueFormatter: ({ value }) => {
      if (value === -Infinity) return "-"
      return value ? format(new Date(value), "PPpp") : "-"
    },
  }

  export const LastUploadDateTime: GridColDef<DataLogger> = {
    field: "readingsUpload[0].createdAt",
    headerName: "Last upload date",
    type: "string",
    minWidth: 200,
    editable: false,
    valueGetter: ({ row }) => {
      const sortedUploads = (row?.readingsUploads || []).sort((a, b) => +new Date(b.createdAt) - +new Date(a.createdAt))
      return sortedUploads[0]?.createdAt || null
    },
    valueFormatter: ({ value }) => value ? format(new Date(value), "PPpp") : "-",
  }

  export const Contractor: GridColDef<DataLogger> = {
    field: "projectSectionsDataLoggers[0].contractor.name",
    headerName: "Contractor",
    type: "string",
    minWidth: 200,
    editable: false,
    valueGetter: ({ row }) => {
      const activeProjectSection = (row?.projectSectionsDataLoggers || []).find((item) => item.isActive)
      return activeProjectSection?.contractor?.name || null
    },
    renderCell: ({ value, row }) => {
      if (!value) return '-'
      const activeProjectSection = (row?.projectSectionsDataLoggers || []).find((item) => item.isActive)
      const contractorId = activeProjectSection?.contractorId
      return (
        <InnerLink to={`/contractors/${contractorId}`}>{value}</InnerLink>
      )
    }
  }

  export const Project: GridColDef<DataLogger> = {
    field: "projectSectionsDataLoggers[0].project.name",
    headerName: "Project",
    type: "string",
    minWidth: 200,
    editable: false,
    valueGetter: ({ row }) => {
      const activeProjectSection = (row?.projectSectionsDataLoggers || []).find((item) => item.isActive)
      return activeProjectSection?.project?.name || null
    },
    renderCell: ({ value, row }) => {
      if (!value) return '-'
      const activeProjectSection = (row?.projectSectionsDataLoggers || []).find((item) => item.isActive)
      const projectId = activeProjectSection?.projectId
      return (
        <InnerLink to={`/projects/${projectId}`}>{value}</InnerLink>
      )
    }
  }

  export const Section: GridColDef<DataLogger> = {
    field: "projectSectionsDataLoggers[0].projectSection.name",
    headerName: "Section",
    type: "string",
    minWidth: 200,
    editable: false,
    valueGetter: ({ row }) => {
      const activeProjectSection = (row?.projectSectionsDataLoggers || []).find((item) => item.isActive)
      return activeProjectSection?.projectSection?.name || null
    },
    renderCell: ({ value, row }) => {
      if (!value) return '-'
      const activeProjectSection = (row?.projectSectionsDataLoggers || []).find((item) => item.isActive)
      const projectSectionId = activeProjectSection?.projectSectionId
      return (
        <InnerLink to={`/sections/${projectSectionId}`}>{value}</InnerLink>
      )
    }
  }

  export const BatteryLevel: GridColDef<DataLogger> = {
    field: "currentData.battery",
    headerName: "Last received battery level",
    type: "string",
    minWidth: 200,
    editable: false,
    valueGetter: ({ row }) => row?.currentData?.battery ?? null,
    valueFormatter: ({ value }) => value == null ? '-' : value,
    cellClassName: ({ value }) => {
      const severity = classifyBatteryLevel(value as number | null)
      return severity ? `MuiDataGrid-cell--health-${severity}` : ''
    },
  }

  export const CreatedAt: GridColDef<DataLogger> = {
    field: "createdAt",
    headerName: "Created at",
    type: "string",
    minWidth: 200,
    editable: false,
    valueFormatter: ({ value }) => value ? format(new Date(value), "PPpp") : "-",
  }

  export const DaysSinceActivation: GridColDef<DataLogger> = {
    field: "daysSinceActivation",
    headerName: "Days since activation",
    type: "number",
    align: "left",
    headerAlign: "left",
    minWidth: 180,
    editable: false,
    valueGetter: ({ row }) => row?.createdAt ? getDaysSinceActivation(row.createdAt) : null,
    valueFormatter: ({ value }) => value == null ? '-' : value,
    cellClassName: ({ row }) => {
      if (!row?.createdAt) return ''
      const activeInterval = (row?.intervalHistory || []).find((item) => item.isActive)
      if (!activeInterval?.intervalInHours) return ''
      const days = getDaysSinceActivation(row.createdAt)
      const capacityDays = getMemoryCapacityDays(activeInterval.intervalInHours)
      const severity = classifyDaysSinceActivation(days, capacityDays)
      return severity ? `MuiDataGrid-cell--health-${severity}` : ''
    },
    renderCell: ({ value, row }) => {
      if (value == null) return '-'
      const intervalInHours = (row?.intervalHistory || []).find((item) => item.isActive)?.intervalInHours
      const tooltip = intervalInHours
        ? `Estimated memory capacity: ~${Math.round(getMemoryCapacityDays(intervalInHours))} days (${DATA_LOGGER_MEMORY_CAPACITY.toLocaleString()} readings at a ${intervalInHours}h interval). Highlighted yellow within 60 days and red within 30 days of that.`
        : 'Estimated memory capacity unknown — no active measurement interval set.'
      return (
        <Tooltip title={tooltip}>
          <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, cursor: 'help' }}>
            {value}
            <InfoOutlinedIcon sx={{ fontSize: 16, color: 'inherit', opacity: 0.6 }} />
          </Box>
        </Tooltip>
      )
    },
  }

  export const Comment: GridColDef<DataLogger> = {
    field: "comment",
    headerName: "Internal comment",
    type: "string",
    minWidth: 180,
    editable: true,
  }
}

export namespace DataLoggerTableColumns {
  export const DeviceID: DynamicColumn<DataLogger> = {
    field: "deviceId",
    headerName: "Device ID",
    type: "string",
  }

  export const Label: DynamicColumn<DataLogger> = {
    field: "projectSectionsDataLoggers.label",
    headerName: "Label",
    type: "custom",
    component: ({ row, cellProps }) => {
      const activeProjectSection = (row?.projectSectionsDataLoggers || []).find((item) => item.isActive)
      const label = activeProjectSection?.label || '-'
      return <TableCell align={cellProps?.align}>{label}</TableCell>
    },
  }

  export const Interval: DynamicColumn<DataLogger> = {
    field: "intervalHistory.intervalInHours",
    headerName: "Interval, hours",
    type: "custom",
    component: ({ row, cellProps }) => {
      const activeInterval = (row?.intervalHistory || []).find((item) => item.isActive)
      const interval = activeInterval?.intervalInHours || '-'
      return <TableCell align={cellProps?.align}>{interval}</TableCell>
    },
  }

  export const MatchReadingsToTimeInterval: DynamicColumn<DataLogger> = {
    field: "isMatchReadingsToTimeInterval",
    headerName: "Match readings to time interval",
    type: "custom",
    component: ({ row, cellProps }) => {
      const isMatchReadingsToTimeInterval = typeof row.isMatchReadingsToTimeInterval === 'boolean' ? (row.isMatchReadingsToTimeInterval ? 'Yes' : 'No') : '-'
      return <TableCell align={cellProps?.align}>{isMatchReadingsToTimeInterval}</TableCell>
    }
  }

  export const Project: DynamicColumn<DataLogger> = {
    field: "projectSectionsDataLoggers.project",
    headerName: "Project",
    type: "custom",
    component: ({ row, cellProps }) => {
      const activeProjectSection = (row?.projectSectionsDataLoggers || []).find((item) => item.isActive)
      const id = activeProjectSection?.projectId
      const name = activeProjectSection?.project?.name || '-'
      return (
        <TableCell align={cellProps?.align}>
          <InnerLink to={`/projects/${id}`}>{name}</InnerLink>
        </TableCell>
      )
    }
  }

  export const ProjectSection: DynamicColumn<DataLogger> = {
    field: "projectSectionsDataLoggers.projectSection",
    headerName: "Project Section",
    type: "custom",
    component: ({ row, cellProps }) => {
      const activeProjectSection = (row?.projectSectionsDataLoggers || []).find((item) => item.isActive)
      const id = activeProjectSection?.projectSectionId
      const name = activeProjectSection?.projectSection?.name || '-'
      return (
        <TableCell align={cellProps?.align}>
          <InnerLink to={`/sections/${id}`}>{name}</InnerLink>
        </TableCell>
      )
    }
  }

  export const Contractor: DynamicColumn<DataLogger> = {
    field: "projectSectionsDataLoggers.contractor",
    headerName: "Contractor",
    type: "custom",
    component: ({ row, cellProps }) => {
      const activeProjectSection = (row?.projectSectionsDataLoggers || []).find((item) => item.isActive)
      const id = activeProjectSection?.contractorId
      const name = activeProjectSection?.contractor?.name || '-'
      return (
        <TableCell align={cellProps?.align}>
          <InnerLink to={`/contractors/${id}`}>{name}</InnerLink>
        </TableCell>
      )
    }
  }
}

export namespace DataLoggerIntervalGridColumns {
  export const Interval: GridColDef<DataLoggerIntervalHistory> = {
    field: "intervalInHours",
    headerName: "Interval, hours",
    type: "string",
    minWidth: 150,
    editable: false,
    valueFormatter: ({ value }) => value || '-',
  }

  export const Status: GridColDef<DataLoggerIntervalHistory> = {
    field: "isActive",
    headerName: "Status",
    type: "string",
    minWidth: 150,
    editable: false,
    valueFormatter: ({ value }) => value ? "Active" : "Inactive",
  }

  export const CreatedAt: GridColDef<DataLoggerIntervalHistory> = {
    field: "createdAt",
    headerName: "Created at",
    type: "string",
    minWidth: 200,
    editable: false,
    valueFormatter: ({ value }) => value ? format(new Date(value), "PPpp") : "-",
  }

  export const EndedAt: GridColDef<DataLoggerIntervalHistory> = {
    field: "endedAt",
    headerName: "Ended at",
    type: "string",
    minWidth: 200,
    editable: false,
    valueFormatter: ({ value }) => value ? format(new Date(value), "PPpp") : "-",
  }
}

export namespace DataLoggerReadingUploadsGridColumns {
  export const UploadDate: GridColDef<DataLoggerReadingUpload> = {
    field: "createdAt",
    headerName: "Upload date",
    type: "string",
    minWidth: 200,
    editable: false,
    valueFormatter: ({ value }) => value ? format(new Date(value), "PPpp") : "-",
  }

  export const DateFrom: GridColDef<DataLoggerReadingUpload> = {
    field: "dateFrom",
    headerName: "First reading date",
    type: "string",
    minWidth: 200,
    editable: false,
    valueFormatter: ({ value }) => value ? format(new Date(value), "PPpp") : "-",
  }

  export const DateTo: GridColDef<DataLoggerReadingUpload> = {
    field: "dateTo",
    headerName: "Last reading date",
    type: "string",
    minWidth: 200,
    editable: false,
    valueFormatter: ({ value }) => value ? format(new Date(value), "PPpp") : "-",
  }

  export const Status: GridColDef<DataLoggerReadingUpload> = {
    field: "status",
    headerName: "Status",
    type: "string",
    minWidth: 150,
    editable: false,
    valueFormatter: ({ value }) => {
      switch (value) {
      case 'created':
        return 'Created'
      case 'backup_saved':
        return 'Backup saved'
      case 'in_progress':
        return 'In progress'
      case 'completed':
        return 'Completed'
      case 'failed':
        return 'Failed'
      default:
        return capitalizeFirstLetter(value)
      }
    },
  }

  export const MatchReadingsToTimeInterval: GridColDef<DataLoggerReadingUpload> = {
    field: "isMatchReadingsToTimeInterval",
    headerName: "Match readings to time interval",
    type: "boolean",
    minWidth: 150,
    editable: false,
  }

  export const ExpectedIntervalInHours: GridColDef<DataLoggerReadingUpload> = {
    field: "expectedIntervalInHours",
    headerName: "Expected interval, hours",
    type: "string",
    minWidth: 150,
    editable: false,
    valueFormatter: ({ value }) => value || '-',
  }

  export const ActualIntervalInHours: GridColDef<DataLoggerReadingUpload> = {
    field: "actualIntervalInHours",
    headerName: "Actual interval, hours",
    type: "string",
    minWidth: 150,
    editable: false,
    valueFormatter: ({ value }) => value || '-',
  }

  export const WrongIntervalAt: GridColDef<DataLoggerReadingUpload> = {
    field: "wrongIntervalAt",
    headerName: "Wrong interval at",
    type: "string",
    minWidth: 200,
    editable: false,
    valueFormatter: ({ value }) => value ? format(new Date(value), "PPpp") : "-",
  }

  export const ReadingsCount: GridColDef<DataLoggerReadingUpload> = {
    field: "readingsCount",
    headerName: "Readings count",
    type: "string",
    minWidth: 150,
    editable: false,
    valueFormatter: ({ value }) => value || '-',
  }

  export const FileName: GridColDef<DataLoggerReadingUpload> = {
    field: "fileName",
    headerName: "File name",
    type: "string",
    minWidth: 450,
    editable: false,
    renderCell: ({ value, row }) => {
      const link = `/json-viewer?link=${encodeURIComponent(row.link as string)}&fileName=${value}`
      return (
        <OuterLink to={link} target="_blank" rel="noreferrer">{value}</OuterLink>
      )
    }
  }

  export const FileNameRawFile: GridColDef<DataLoggerReadingUpload> = {
    field: "fileNameRawFile",
    headerName: "Raw data",
    type: "string",
    minWidth: 450,
    editable: false,
    renderCell: ({ value, row }) => {
      if (!value) return '-'
      const link = `/json-viewer?link=${encodeURIComponent(row.linkRawData as string)}&fileName=${value}`
      return (
        <OuterLink to={link} target="_blank" rel="noreferrer">{value}</OuterLink>
      )
    }
  }
}

export namespace DataLoggerDashboardColumns {
  export const SensorsNumber: DynamicColumn<DataLoggerDashboardStatistics> = {
    field: "sensorsNumber",
    headerName: "Total",
    type: "digit",
  }

  export const InUseSensorsNumber: DynamicColumn<DataLoggerDashboardStatistics> = {
    field: "inUseSensorsNumber",
    headerName: "In use",
    type: "digit",
  }
}
